import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Plus, Trash2, Edit, X, Camera, ChevronLeft, Search,
  Gauge, Fuel, Droplets, Clock, User, Activity, FileText, Building2, Filter
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COMBUSTIVEL_TIPOS = ['Diesel Comum', 'Diesel S10'];
const STATUS_OPTIONS = [
  { value: 'ativo', label: 'ATIVO', color: 'bg-emerald-500' },
  { value: 'inativo', label: 'INATIVO', color: 'bg-gray-400' },
  { value: 'manutencao', label: 'MANUTENCAO', color: 'bg-amber-500' },
];

const EMPTY_FORM = {
  name: '', combustivel_tipo: 'Diesel Comum',
  capacidade_tanque: '', media_consumo_hora: '',
  horimetro_inicial: '', horimetro_final: '',
  maquinista_nome: '', status: 'ativo', secretaria_id: ''
};

export default function MaquinasPage() {
  const [maquinas, setMaquinas] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedMaquina, setSelectedMaquina] = useState(null);
  const [painelData, setPainelData] = useState(null);
  const [loadingPainel, setLoadingPainel] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBlobUrls, setPhotoBlobUrls] = useState({});
  const [showHorForm, setShowHorForm] = useState(false);
  const [horForm, setHorForm] = useState({
    horimetro_inicial: '', horimetro_final: '',
    litros_abastecidos: '', data_registro: '', observacao: ''
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSecretaria, setFilterSecretaria] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [maqRes, secRes] = await Promise.all([
        api.get('/maquinas'),
        api.get('/secretarias')
      ]);
      setMaquinas(maqRes.data || []);
      setSecretarias(secRes.data || []);
    } catch { toast.error('Erro ao carregar dados'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    maquinas.forEach(m => {
      if (m.photo_url && !photoBlobUrls[m.id]) {
        api.get(m.photo_url, { responseType: 'blob' })
          .then(res => {
            const url = URL.createObjectURL(res.data);
            setPhotoBlobUrls(prev => ({ ...prev, [m.id]: url }));
          })
          .catch(() => {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maquinas]);

  // Filtered and sorted list
  // Compute fuel remaining for each machine
  const computePainel = (m) => {
    const horas = Math.max(0, (m.horimetro_final || 0) - (m.horimetro_inicial || 0));
    const consumido = horas * (m.media_consumo_hora || 0);
    const restante = Math.max(0, (m.capacidade_tanque || 0) - consumido);
    return { horas, consumido, restante };
  };

  const filteredMaquinas = useMemo(() => {
    return maquinas
      .filter(m => {
        const q = searchQuery.toLowerCase();
        const matchSearch = !q || (m.name || '').toLowerCase().includes(q) || (m.maquinista_nome || '').toLowerCase().includes(q);
        const matchSec = !filterSecretaria || m.secretaria_id === filterSecretaria;
        return matchSearch && matchSec;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }, [maquinas, searchQuery, filterSecretaria]);

  const getSecretariaNome = (secId) => {
    const sec = secretarias.find(s => s.id === secId);
    return sec ? sec.name : '';
  };

  const handleExportSecretariaPdf = async () => {
    if (!filterSecretaria) return toast.error('Selecione uma secretaria');
    try {
      const res = await api.get(`/maquinas/secretaria-pdf/${filterSecretaria}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const secName = getSecretariaNome(filterSecretaria).replace(/\s/g, '_').toUpperCase();
      a.download = `MAQUINAS_${secName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exportado!');
    } catch (err) {
      const msg = err.response?.status === 404 ? 'Nenhuma maquina encontrada para esta secretaria' : 'Erro ao gerar PDF';
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPhotoFile(null);
    setPhotoPreview(null);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Nome da maquina obrigatorio');
    const payload = {
      ...form,
      capacidade_tanque: parseFloat(form.capacidade_tanque) || 0,
      media_consumo_hora: parseFloat(form.media_consumo_hora) || 0,
      horimetro_inicial: parseFloat(form.horimetro_inicial) || 0,
      horimetro_final: parseFloat(form.horimetro_final) || 0,
    };
    try {
      let maqId = editingId;
      if (editingId) {
        await api.put(`/maquinas/${editingId}`, payload);
        toast.success('Maquina atualizada!');
      } else {
        const res = await api.post('/maquinas', payload);
        maqId = res.data.id;
        toast.success('Maquina cadastrada!');
      }
      if (photoFile && maqId) {
        const fd = new FormData();
        fd.append('file', photoFile);
        await api.post(`/maquinas/${maqId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (m) => {
    setForm({
      name: m.name || '', combustivel_tipo: m.combustivel_tipo || 'Diesel Comum',
      capacidade_tanque: m.capacidade_tanque || '', media_consumo_hora: m.media_consumo_hora || '',
      horimetro_inicial: m.horimetro_inicial || '', horimetro_final: m.horimetro_final || '',
      maquinista_nome: m.maquinista_nome || '', status: m.status || 'ativo',
      secretaria_id: m.secretaria_id || ''
    });
    setEditingId(m.id);
    setShowForm(true);
    setPhotoPreview(photoBlobUrls[m.id] || null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta maquina?')) return;
    try {
      await api.delete(`/maquinas/${id}`);
      toast.success('Maquina excluida');
      fetchData();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const openPainel = async (m) => {
    setSelectedMaquina(m);
    setLoadingPainel(true);
    try {
      const res = await api.get(`/maquinas/${m.id}/painel`);
      setPainelData(res.data);
    } catch { toast.error('Erro ao carregar painel'); }
    setLoadingPainel(false);
  };

  // Horimetro handlers
  const handleAddHor = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/maquinas/${selectedMaquina.id}/horimetros`, {
        maquina_id: selectedMaquina.id,
        horimetro_inicial: parseFloat(horForm.horimetro_inicial) || 0,
        horimetro_final: parseFloat(horForm.horimetro_final) || 0,
        litros_abastecidos: parseFloat(horForm.litros_abastecidos) || 0,
        data_registro: horForm.data_registro,
        observacao: horForm.observacao,
      });
      toast.success('Registro adicionado');
      setShowHorForm(false);
      setHorForm({ horimetro_inicial: '', horimetro_final: '', litros_abastecidos: '', data_registro: '', observacao: '' });
      openPainel(selectedMaquina);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleDeleteHor = async (horId) => {
    if (!window.confirm('Excluir este registro?')) return;
    try {
      await api.delete(`/maquinas/${selectedMaquina.id}/horimetros/${horId}`);
      toast.success('Registro excluido');
      openPainel(selectedMaquina);
    } catch { toast.error('Erro ao excluir registro'); }
  };

  const handleDownloadPdf = async (maqId, maqName) => {
    try {
      const res = await api.get(`/maquinas/${maqId}/relatorio-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RELATORIO_${maqName.replace(/\s/g, '_').toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF gerado!');
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  const getStatusBadge = (status) => {
    const s = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
    return <span className={`${s.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase`}>{s.label}</span>;
  };

  const fmt = (v, d = 1) => {
    const n = parseFloat(v) || 0;
    return n.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });
  };

  // Build chart data from horimetro records
  const chartData = useMemo(() => {
    if (!painelData?.horimetros) return [];
    return [...painelData.horimetros].reverse().map((h, i) => ({
      nome: h.data_registro || `Reg ${i + 1}`,
      horas: h.horas_trabalhadas || 0,
      litros: h.litros_abastecidos || 0,
      consumo: h.consumo_por_hora || 0,
    }));
  }, [painelData]);

  // ============================================================
  // PAINEL DA MAQUINA (Dashboard profissional)
  // ============================================================
  if (selectedMaquina) {
    const maq = painelData?.maquina || selectedMaquina;
    const secNome = getSecretariaNome(maq.secretaria_id);
    return (
      <div className="space-y-4" data-testid="maquina-painel">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedMaquina(null); setPainelData(null); }} data-testid="back-to-list">
            <ChevronLeft size={18} /> VOLTAR
          </Button>
          <h2 className="text-lg font-bold text-gray-800 uppercase">{maq.name}</h2>
          {getStatusBadge(maq.status)}
          {secNome && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{secNome}</span>}
          <div className="ml-auto">
            <Button size="sm" onClick={() => handleDownloadPdf(maq.id, maq.name)} data-testid="download-pdf-btn"
              className="bg-[#003366] hover:bg-[#1E3A5F] text-white">
              <FileText size={16} className="mr-2" /> IMPRIMIR RELATORIO
            </Button>
          </div>
        </div>

        {loadingPainel ? (
          <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
        ) : painelData ? (
          <div className="space-y-4">
            {/* Foto + KPIs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-1">
                <CardContent className="p-4 flex flex-col items-center justify-center gap-2">
                  {photoBlobUrls[maq.id] ? (
                    <img src={photoBlobUrls[maq.id]} alt={maq.name} className="max-h-[220px] w-full object-contain rounded-lg" data-testid="maquina-photo" />
                  ) : (
                    <div className="h-[220px] w-full bg-gray-100 rounded-lg flex items-center justify-center">
                      <Camera size={48} className="text-gray-300" />
                    </div>
                  )}
                  {maq.maquinista_nome && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                      <User size={14} className="text-blue-600" />
                      <span className="uppercase">{maq.maquinista_nome}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-bold text-gray-700">INDICADORES CALCULADOS</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { icon: Clock, label: 'Horas Trabalhadas', value: `${fmt(painelData.horas_trabalhadas)} H`, color: 'text-blue-600', bg: 'bg-blue-50' },
                      { icon: Activity, label: 'Comb. Consumido', value: `${fmt(painelData.combustivel_consumido)} L`, color: 'text-red-600', bg: 'bg-red-50' },
                      { icon: Fuel, label: 'Comb. Restante', value: `${fmt(painelData.combustivel_restante)} L`, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                      { icon: Droplets, label: 'Faltante p/ Completar', value: `${fmt(painelData.faltante_para_completar)} L`, color: 'text-amber-600', bg: 'bg-amber-50' },
                    ].map((kpi, i) => (
                      <div key={i} className={`${kpi.bg} rounded-lg p-3`}>
                        <div className="flex items-center gap-1 mb-1">
                          <kpi.icon size={14} className={kpi.color} />
                          <span className="text-[10px] font-medium text-gray-600 uppercase">{kpi.label}</span>
                        </div>
                        <p className={`text-lg font-bold ${kpi.color}`} data-testid={`kpi-maquina-${i}`}>{kpi.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs text-gray-500">
                    <div><span className="font-medium">Combustivel:</span> {maq.combustivel_tipo}</div>
                    <div><span className="font-medium">Capacidade:</span> {fmt(maq.capacidade_tanque)} L</div>
                    <div><span className="font-medium">Hor. Inicial:</span> {fmt(maq.horimetro_inicial)} H</div>
                    <div><span className="font-medium">Hor. Final:</span> {fmt(maq.horimetro_final)} H</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Grafico de Consumo */}
            {chartData.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-bold text-gray-700">HISTORICO DE CONSUMO</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="nome" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="horas" name="Horas Trab." fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="litros" name="Litros Abast." fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Historico de Horimetros */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-sm font-bold text-gray-700">REGISTROS DE HORIMETRO</CardTitle>
                  <Button size="sm" onClick={() => setShowHorForm(!showHorForm)} data-testid="add-horimetro-btn">
                    <Plus size={14} className="mr-1" /> REGISTRAR
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showHorForm && (
                  <form onSubmit={handleAddHor} className="bg-gray-50 rounded-lg p-3 mb-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid="horimetro-form">
                    <div><Label className="text-[10px]">Hor. Inicial</Label><Input type="number" step="0.1" value={horForm.horimetro_inicial} onChange={e => setHorForm(p => ({ ...p, horimetro_inicial: e.target.value }))} className="h-8 text-xs" required data-testid="hor-ini-input" /></div>
                    <div><Label className="text-[10px]">Hor. Final</Label><Input type="number" step="0.1" value={horForm.horimetro_final} onChange={e => setHorForm(p => ({ ...p, horimetro_final: e.target.value }))} className="h-8 text-xs" required data-testid="hor-fin-input" /></div>
                    <div><Label className="text-[10px]">Litros Abast.</Label><Input type="number" step="0.1" value={horForm.litros_abastecidos} onChange={e => setHorForm(p => ({ ...p, litros_abastecidos: e.target.value }))} className="h-8 text-xs" data-testid="hor-litros-input" /></div>
                    <div><Label className="text-[10px]">Data</Label><Input type="date" value={horForm.data_registro} onChange={e => setHorForm(p => ({ ...p, data_registro: e.target.value }))} className="h-8 text-xs" data-testid="hor-data-input" /></div>
                    <div className="flex items-end gap-1"><Button type="submit" size="sm" className="h-8 text-xs" data-testid="hor-submit-btn">SALVAR</Button><Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => setShowHorForm(false)}><X size={14} /></Button></div>
                  </form>
                )}
                {painelData.horimetros?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#003366] text-white">
                        <th className="px-2 py-1.5 text-left rounded-tl-lg">Data</th>
                        <th className="px-2 py-1.5 text-right">Hor. Ini</th>
                        <th className="px-2 py-1.5 text-right">Hor. Fin</th>
                        <th className="px-2 py-1.5 text-right">Horas</th>
                        <th className="px-2 py-1.5 text-right">Litros</th>
                        <th className="px-2 py-1.5 text-right">L/H</th>
                        <th className="px-2 py-1.5 text-center rounded-tr-lg">Acao</th>
                      </tr></thead>
                      <tbody>
                        {painelData.horimetros.map((h, idx) => (
                          <tr key={h.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-2 py-1.5">{h.data_registro || '-'}</td>
                            <td className="px-2 py-1.5 text-right">{fmt(h.horimetro_inicial)}</td>
                            <td className="px-2 py-1.5 text-right">{fmt(h.horimetro_final)}</td>
                            <td className="px-2 py-1.5 text-right font-medium text-blue-600">{fmt(h.horas_trabalhadas)}</td>
                            <td className="px-2 py-1.5 text-right font-medium text-emerald-600">{fmt(h.litros_abastecidos)}</td>
                            <td className="px-2 py-1.5 text-right">{fmt(h.consumo_por_hora, 2)}</td>
                            <td className="px-2 py-1.5 text-center">
                              <button onClick={() => handleDeleteHor(h.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-hor-${h.id}`}><Trash2 size={12} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-gray-400 text-sm text-center py-4">Nenhum registro de horimetro</p>}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    );
  }

  // ============================================================
  // LISTA DE MAQUINAS
  // ============================================================
  return (
    <div className="space-y-4" data-testid="maquinas-page">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Maquinas</h2>
          <p className="text-slate-600">Gestao de maquinas e equipamentos</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-maquina-btn">
          <Plus size={16} className="mr-1" /> NOVA MAQUINA
        </Button>
      </div>

      {/* Filtros: Busca + Secretaria + Exportar PDF */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input placeholder="Buscar por nome, maquinista..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-maquinas" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400" />
          <select value={filterSecretaria} onChange={(e) => setFilterSecretaria(e.target.value)}
            className="h-10 px-3 rounded-md border text-sm min-w-[200px]" data-testid="filter-secretaria-maquinas">
            <option value="">Todas as Secretarias</option>
            {secretarias.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')).map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        {filterSecretaria && (
          <Button size="sm" onClick={handleExportSecretariaPdf}
            className="bg-[#003366] hover:bg-[#1E3A5F] text-white" data-testid="export-secretaria-pdf-btn">
            <FileText size={16} className="mr-1" /> EXPORTAR PDF
          </Button>
        )}
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => resetForm()}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="maquina-form">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-lg text-[#003366]">{editingId ? 'EDITAR MAQUINA' : 'NOVA MAQUINA'}</h3>
              <button onClick={resetForm}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div><Label>Nome da Maquina *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Retroescavadeira CAT" required data-testid="maquina-name-input" /></div>
              <div>
                <Label>Secretaria</Label>
                <select value={form.secretaria_id} onChange={e => setForm(p => ({ ...p, secretaria_id: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border text-sm" data-testid="maquina-secretaria-select">
                  <option value="">Selecione a secretaria...</option>
                  {secretarias.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Foto da Maquina</Label>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors">
                    <Camera size={16} /> Escolher Foto
                    <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" data-testid="maquina-photo-input" />
                  </label>
                  {photoPreview && <img src={photoPreview} alt="preview" className="h-16 w-16 object-cover rounded-lg border" />}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de Combustivel</Label>
                  <select value={form.combustivel_tipo} onChange={e => setForm(p => ({ ...p, combustivel_tipo: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border text-sm" data-testid="maquina-combustivel-select">
                    {COMBUSTIVEL_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Capacidade Tanque (L)</Label>
                  <Input type="number" step="0.1" value={form.capacidade_tanque} onChange={e => setForm(p => ({ ...p, capacidade_tanque: e.target.value }))} placeholder="Ex: 200" data-testid="maquina-capacidade-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Media Consumo/Hora (L/H)</Label>
                  <Input type="number" step="0.01" value={form.media_consumo_hora} onChange={e => setForm(p => ({ ...p, media_consumo_hora: e.target.value }))} placeholder="Ex: 1.5" data-testid="maquina-media-input" />
                </div>
                <div>
                  <Label>Status</Label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border text-sm" data-testid="maquina-status-select">
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Horimetro Inicial (H)</Label>
                  <Input type="number" step="0.1" value={form.horimetro_inicial} onChange={e => setForm(p => ({ ...p, horimetro_inicial: e.target.value }))} placeholder="Leitura ult. abastecimento" data-testid="maquina-hor-ini-input" />
                </div>
                <div>
                  <Label>Horimetro Final (H)</Label>
                  <Input type="number" step="0.1" value={form.horimetro_final} onChange={e => setForm(p => ({ ...p, horimetro_final: e.target.value }))} placeholder="Leitura atual" data-testid="maquina-hor-fin-input" />
                </div>
              </div>
              <div>
                <Label>Maquinista Responsavel</Label>
                <Input value={form.maquinista_nome} onChange={e => setForm(p => ({ ...p, maquinista_nome: e.target.value }))} placeholder="Nome do maquinista" data-testid="maquina-maquinista-input" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="flex-1" data-testid="maquina-submit-btn">{editingId ? 'ATUALIZAR' : 'CADASTRAR'}</Button>
                <Button type="button" variant="outline" onClick={resetForm}>CANCELAR</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cards */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
      ) : filteredMaquinas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Gauge size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">{searchQuery || filterSecretaria ? 'Nenhuma maquina encontrada' : 'Nenhuma maquina cadastrada'}</p>
            {!searchQuery && !filterSecretaria && (
              <Button className="mt-4" onClick={() => setShowForm(true)} data-testid="empty-add-btn">
                <Plus size={16} className="mr-1" /> Cadastrar Primeira Maquina
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="maquinas-grid">
          {filteredMaquinas.map(m => {
            const secNome = getSecretariaNome(m.secretaria_id);
            const { restante, consumido } = computePainel(m);
            const cap = m.capacidade_tanque || 0;
            const pctRestante = cap > 0 ? Math.min(100, Math.round((restante / cap) * 100)) : 0;
            const barColor = pctRestante > 50 ? 'bg-emerald-500' : pctRestante > 20 ? 'bg-amber-500' : 'bg-red-500';
            return (
              <Card key={m.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group" data-testid={`maquina-card-${m.id}`}>
                <div onClick={() => openPainel(m)} className="relative">
                  {photoBlobUrls[m.id] ? (
                    <img src={photoBlobUrls[m.id]} alt={m.name} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <Gauge size={48} className="text-gray-300" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2">{getStatusBadge(m.status)}</div>
                  {secNome && <div className="absolute bottom-2 left-2 bg-white/90 text-[10px] font-semibold text-[#003366] px-2 py-0.5 rounded">{secNome}</div>}
                </div>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 cursor-pointer" onClick={() => openPainel(m)}>
                      <h3 className="font-bold text-sm uppercase text-gray-800 group-hover:text-blue-600 transition-colors">{m.name}</h3>
                      <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-1">
                        <User size={10} /> <span className="uppercase font-medium">{m.maquinista_nome || 'SEM CONDUTOR'}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500">
                        <span className="flex items-center gap-1"><Fuel size={10} /> {m.combustivel_tipo}</span>
                        <span className="flex items-center gap-1"><Gauge size={10} /> {fmt(m.capacidade_tanque || 0)} L</span>
                      </div>
                      {/* Barra de combustível restante */}
                      <div className="mt-2">
                        <div className="flex justify-between text-[10px] mb-0.5">
                          <span className="text-gray-500 font-medium">Comb. Restante</span>
                          <span className={`font-bold ${pctRestante > 50 ? 'text-emerald-600' : pctRestante > 20 ? 'text-amber-600' : 'text-red-600'}`}>{fmt(restante)} L ({pctRestante}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${pctRestante}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-1">
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(m); }} className="p-1 hover:bg-gray-100 rounded" data-testid={`edit-maquina-${m.id}`}>
                        <Edit size={14} className="text-blue-500" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }} className="p-1 hover:bg-gray-100 rounded" data-testid={`delete-maquina-${m.id}`}>
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
